import getLibraryItemInfo from '@/requests/library/getLibraryItemInfo';
import { getAppStore, setAppStore } from '@/store';
import { Screen, type IInput } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type PresetDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Preset }>;

/**
 * presetId deeplink: load the named preset and navigate to the create screen.
 * Sets hasAutoSelectedPreset early so the library auto-select doesn't race
 * with the preset fetch.
 */
const presetHandler = async ({ presetId }: PresetDeeplink): Promise<void> => {
  setAppStore({
    hasAutoSelectedPreset: true,
    isRemoteProject: true,
    screen: Screen.Create,
    isPresetLoading: true,
  });

  try {
    const preset = await getLibraryItemInfo(presetId);
    if (!preset) {
      setAppStore({
        hasAutoSelectedPreset: false,
        screen: Screen.Library,
        isPresetLoading: false,
      });
      return;
    }

    // If a higher-priority handler (e.g. projectJsonHandler) already set the
    // preset, don't clobber it.
    const { selectedPreset } = getAppStore();
    if (selectedPreset?.flowId === preset.flowId) {
      setAppStore({ isPresetLoading: false });
      return;
    }

    const inputs: IInput[] = [
      ...(preset.textItems ?? []),
      ...(preset.uploadItems ?? []),
    ];

    setAppStore({
      selectedPreset: preset,
      inputs,
      isPresetLoading: false,
      screen: Screen.Create,
      ...(preset.category && { activeTabSlug: preset.category }),
      shouldScrollToActiveTab: true,
    });
  } catch {
    setAppStore({
      hasAutoSelectedPreset: false,
      screen: Screen.Library,
      isPresetLoading: false,
    });
  }
};

export default presetHandler;
